import { zodResolver } from "@hookform/resolvers/zod";
import { Deferred, router, usePage } from "@inertiajs/react";
import { format } from "date-fns";
import {
	AlertCircle,
	CalendarIcon,
	CheckCircleIcon,
	CheckIcon,
	EllipsisVerticalIcon,
	IdCard,
	LoaderIcon,
	MapPin,
	PencilIcon,
	PlusIcon,
	SquarePen,
	Trash2,
	X,
} from "lucide-react";
import {
	Fragment,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Drawer as DrawerPrimitive } from "vaul";
import { z } from "zod";
import {
	ADDRESS_ENTRY_SCHEMA,
	AddressEntryFields,
	type AddressEntryValues,
} from "@/components/shared/address-entry-fields";
import { LoadingBasic } from "@/components/shared/loading";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Calendar, type CalendarProps } from "@/components/ui/calendar";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from "@/components/ui/item";
import { PhoneInput } from "@/components/ui/phone-input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { getGenderIcon } from "@/hooks/use-gender";
import { DOB_YEAR_RANGE, GENDERS } from "@/lib/constants";
import { isValidPhone, PHONE_FORMAT_MESSAGE } from "@/lib/phone";
import { calculateAge, cn, populateQueryParams } from "@/lib/utils";
import { idSchema } from "@/lib/validation";
import type { PatientIndexGlobalPageProps } from "@/pages/AdminPortal/Patient/Index";
import type { PatientActiveAddress } from "@/types/admin-portal/patient";
import AddAddressSection from "./form-create";

const ADDRESS_FORM_SCHEMA = ADDRESS_ENTRY_SCHEMA;
type AddressFormSchema = AddressEntryValues;

const FORM_SCHEMA = z.object({
	contact: z.object({
		id: idSchema,
		contactName: z.string().min(1, "Contact name is required"),
		contactPhone: z
			.string()
			.min(1, { message: "Contact phone number is required" })
			.refine(isValidPhone, { message: PHONE_FORMAT_MESSAGE }),
		email: z.string().email("Invalid email").or(z.literal("")).optional(),
		miitelLink: z.string().url("MiiTel link must be a valid URL").optional(),
	}),
	profile: z.object({
		id: idSchema,
		fullName: z.string().min(1, "Patient full name is required"),
		dateOfBirth: z.coerce
			.date()
			// Ensure the date is in the past
			.refine((date) => date < new Date(), {
				message: "Date of birth must be in the past",
			}),
		age: z.coerce.number().min(0),
		gender: z.enum(GENDERS, {
			required_error: "Need to select a patient gender",
		}),
	}),
});
type FormSchema = z.infer<typeof FORM_SCHEMA>;

type PatientUpdatePayload = {
	profile: Pick<FormSchema["profile"], "id" | "dateOfBirth" | "gender"> & {
		name: string;
	};
};

export interface FormEditProps {
	open: boolean;
	onOpenChange?: (open: boolean) => void;
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}
const FormEdit = memo(function Component({
	open,
	onOpenChange,
	patient,
}: FormEditProps) {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t: tpef } = useTranslation("patients", { keyPrefix: "edit_form" });
	const { props: globalProps, url: pageURL } =
		usePage<PatientIndexGlobalPageProps>();

	// ===== STATE MANAGEMENT =====
	const [isLoading, setIsLoading] = useState(false);
	const leftColumnRef = useRef<HTMLDivElement | null>(null);

	// ===== FORM CONFIGURATION =====
	const formDefaultvalues = useMemo(() => {
		const { contact } = patient;

		return {
			contact: {
				id: contact?.id || "",
				contactName: contact?.contactName || "",
				contactPhone: contact?.contactPhone || "",
				email: contact?.email || "",
				miitelLink: contact?.miitelLink || undefined,
			},
			profile: {
				id: patient.id,
				fullName: patient.name,
				dateOfBirth: new Date(patient.dateOfBirth),
				age: patient.age,
				gender: patient.gender,
			},
		} satisfies FormSchema;
	}, [patient]);

	const form = useForm({
		resolver: zodResolver(FORM_SCHEMA),
		defaultValues: { ...formDefaultvalues },
		mode: "onChange",
	});

	// ===== EVENT HANDLERS =====
	const onSubmit = useCallback(
		(values: FormSchema) => {
			console.group();
			console.log("Starting process to reschedule the appointment...");

			// define the url
			const id = patient.id;
			const baseURL =
				globalProps.adminPortal.router.adminPortal.patientManagement.index;
			const submitURL = `${baseURL}/${id}`;
			// populate current query params
			const { queryParams } = populateQueryParams(pageURL);
			// generate the submit form url with the source query params
			const { fullUrl } = populateQueryParams(submitURL, queryParams);

			// define the submit config
			const submitConfig = {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: [
					"adminPortal",
					"flash",
					"errors",
					"patients",
					"selectedPatient",
					"optionsData",
				],
				onStart: () => setIsLoading(true),
				onFinish: () => {
					setTimeout(() => setIsLoading(false), 250);
				},
			} satisfies Parameters<typeof router.put>["2"];

			// define the payload
			// * contact is intentionally excluded: it has its own save button in the
			// * nested contact drawer, and sending a legacy contact with a blank phone
			// * here would fail the PatientContact presence validation
			const { profile } = values;
			const payload = deepTransformKeysToSnakeCase({
				patient: {
					profile: {
						id: profile.id,
						dateOfBirth: profile.dateOfBirth,
						gender: profile.gender,
						name: profile.fullName,
					},
				} satisfies PatientUpdatePayload,
			});

			router.put(fullUrl, payload, submitConfig);

			console.log("Appointment successfully rescheduled...");
			console.groupEnd();
		},
		[
			globalProps.adminPortal.router.adminPortal.patientManagement.index,
			patient.id,
			pageURL,
		],
	);

	// ===== COMPUTED VALUES =====
	const errorsServerValidation = useMemo(
		() => (globalProps?.errors?.fullMessages as unknown as string[]) || null,
		[globalProps?.errors?.fullMessages],
	);

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
				className="flex flex-col rounded-t-[10px] mt-24 max-h-[90%] fixed bottom-0 left-0 right-0 outline-none bg-card"
			>
				<div className="flex-1 overflow-y-auto overflow-x-hidden">
					<div className="w-full lg:max-w-5xl mx-auto min-w-0">
						<DrawerHeader>
							<DrawerTitle>{tpef("title")}</DrawerTitle>
							<DrawerDescription className="max-w-xs">
								{tpef("description")}
							</DrawerDescription>
						</DrawerHeader>

						<Form {...form}>
							<form
								onSubmit={async (event) => {
									// * validate the profile subtree only — the contact fields live in
									// * their own nested drawer with their own save button, and legacy
									// * patients with a blank contact phone must still be editable here
									event.preventDefault();
									if (await form.trigger("profile")) onSubmit(form.getValues());
								}}
							>
								<Suspense fallback={<LoadingBasic columnBased={true} />}>
									<div
										className={
											"grid items-start grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 p-4 min-w-0 overflow-hidden"
										}
									>
										<div
											className="grid gap-4 md:col-span-5"
											ref={leftColumnRef}
										>
											<ContactSection />

											<Separator className="mt-4" />

											<ProfileSection />
										</div>

										<Separator className="mt-4 md:hidden" />

										<div className="md:col-span-7">
											<AddressSection patient={patient} />
										</div>

										{/* for showing the alert error if there's any server validation error */}
										{!!errorsServerValidation?.length && (
											<Alert
												variant="destructive"
												className="mt-6 col-span-full motion-preset-rebound-down"
											>
												<AlertCircle className="size-4" />
												<AlertTitle className="text-xs">Error</AlertTitle>
												<AlertDescription className="text-xs">
													<ul className="list-disc">
														{errorsServerValidation?.map((error) => (
															<li key={error}>{error}</li>
														))}
													</ul>
												</AlertDescription>
											</Alert>
										)}
									</div>
								</Suspense>

								<DrawerFooter className="gap-4 lg:flex-row lg:gap-2 lg:justify-end">
									<Button
										type="submit"
										size="lg"
										disabled={isLoading || !!form.formState.errors.profile}
									>
										{isLoading ? (
											<>
												<LoaderIcon className="animate-spin" />
												<span>{td("modal.wait")}</span>
											</>
										) : (
											<span>{td("modal.save")}</span>
										)}
									</Button>

									<DrawerClose asChild>
										<Button
											disabled={isLoading}
											variant="outline"
											type="button"
											size="lg"
											className="shadow-none bg-card"
										>
											{td("modal.close")}
										</Button>
									</DrawerClose>
								</DrawerFooter>
							</form>
						</Form>
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
});
export default FormEdit;

const ContactSection = memo(function Component() {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t: tp } = useTranslation("patients");
	const { t: tpef } = useTranslation("patients", { keyPrefix: "edit_form" });
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const { t: tpc } = useTranslation("appointments-form", {
		keyPrefix: "patient_contact",
	});

	// ===== STATE MANAGEMENT =====
	const [isLoading, setIsLoading] = useState(false);
	const [isOpen, setisOpen] = useState(false);

	// ===== FORM & DATA =====
	const form = useFormContext<FormSchema>();
	const { props: globalProps, url: pageURL } =
		usePage<PatientIndexGlobalPageProps>();
	const watchContact = useWatch({ control: form.control, name: "contact" });

	// ===== EVENT HANDLERS =====
	const onSave = useCallback(async () => {
		setIsLoading(true);
		try {
			console.log("Updating patient contact:", {
				contactId: watchContact.id,
				...watchContact,
			});

			// Get patient ID from form context
			const patientId = form.getValues("profile.id");

			// Generate the submit URL
			const baseURL =
				globalProps.adminPortal.router.adminPortal.patientManagement.index;
			const submitURL = `${baseURL}/${patientId}`;
			const { queryParams } = populateQueryParams(pageURL);
			const { fullUrl } = populateQueryParams(submitURL, queryParams);

			// Define the submit config
			const submitConfig = {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "patients", "selectedPatient"],
				onSuccess: (_page: any) => {
					// Close the contact drawer but keep parent drawer open
					setisOpen(false);

					// Show success toast
					toast.success("Contact updated successfully");
				},
				onError: (errors: any) => {
					console.error("Failed to update contact:", errors);
					toast.error("Failed to update contact");
				},
				onFinish: () => {
					// Always close drawer when request finishes
					setisOpen(false);
				},
			} as any;

			// Define the payload
			const payload = deepTransformKeysToSnakeCase({
				patient: {
					contact: {
						id: watchContact.id,
						contactName: watchContact.contactName,
						contactPhone: watchContact.contactPhone,
						email: watchContact.email,
						miitelLink: watchContact.miitelLink,
					},
					profile: {
						id: patientId,
						name: form.getValues("profile.fullName"),
						dateOfBirth: form.getValues("profile.dateOfBirth"),
						gender: form.getValues("profile.gender"),
					},
				},
			});

			// Make the API call using Inertia.js
			router.put(fullUrl, payload, submitConfig);

			console.log("Contact update submitted...");
		} catch (error) {
			console.error("Failed to update contact:", error);
			toast.error("Failed to update contact");
		} finally {
			setIsLoading(false);
		}
	}, [watchContact, form, globalProps, pageURL]);

	return (
		<div className="grid gap-2">
			<div className="flex items-center justify-between">
				<p className="text-xs font-semibold tracking-wider text-muted-foreground/75 uppercase">
					{tpl("contact")}
				</p>

				<Button
					variant="link"
					size="sm"
					effect="expandIcon"
					iconPlacement="left"
					type="button"
					className="p-0"
					icon={SquarePen}
					onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();

						setisOpen(true);
					}}
				>
					{tp("button.edit")}
				</Button>
			</div>

			<div className="p-3 border rounded-md shadow-inner border-border bg-input col-span-full">
				<div className="flex items-center justify-start gap-3">
					<Avatar className="border rounded-lg border-black/10 bg-muted size-8">
						<AvatarImage src="#" />
						<AvatarFallback>
							<IdCard className="flex-shrink-0 size-4 text-muted-foreground/75" />
						</AvatarFallback>
					</Avatar>

					<div className="grid text-sm line-clamp-1">
						<p className="font-semibold uppercase truncate">
							{watchContact.contactName}
						</p>
					</div>
				</div>
			</div>

			<DrawerPrimitive.NestedRoot
				open={isOpen}
				onOpenChange={(value) => {
					setisOpen(value);
				}}
				setBackgroundColorOnScale
			>
				<DrawerContent
					onInteractOutside={(event) => {
						event.preventDefault();
					}}
					className="flex flex-col rounded-t-[10px] mt-24 max-h-[85%] fixed bottom-0 left-0 right-0 outline-none bg-card"
				>
					<div className="flex-1 overflow-y-auto">
						<div className="w-full max-w-sm mx-auto">
							<DrawerHeader>
								<DrawerTitle>{tpef("contact.title")}</DrawerTitle>
							</DrawerHeader>

							<div className="grid gap-4 p-4">
								<FormField
									control={form.control}
									name="contact.contactName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{tpc("fields.name.label")}</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="text"
													autoComplete="name"
													placeholder={tpc("fields.name.placeholder")}
													className="shadow-inner bg-input"
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="contact.contactPhone"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{tpc("fields.contact_number.label")}
											</FormLabel>
											<FormControl>
												<PhoneInput
													{...field}
													international
													placeholder={tpc("fields.contact_number.placeholder")}
													defaultCountry="ID"
													autoComplete="tel"
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="contact.email"
									render={({ field }) => (
										<FormItem className="col-span-1">
											<FormLabel>
												{tpc("fields.email.label")}{" "}
												<span className="text-sm italic font-light">
													- (optional)
												</span>
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="email"
													autoComplete="email"
													placeholder={tpc("fields.email.placeholder")}
													className="shadow-inner bg-input"
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="contact.miitelLink"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{tpc("fields.miitel_link.label")}{" "}
												<span className="text-sm italic font-light">
													- (optional)
												</span>
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="url"
													placeholder={tpc("fields.miitel_link.placeholder")}
													className="shadow-inner bg-input"
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<DrawerFooter className="gap-4">
								<Button
									type="button"
									size="lg"
									disabled={isLoading || !form.formState.isValid}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();

										onSave();
									}}
								>
									{isLoading ? (
										<>
											<LoaderIcon className="animate-spin" />
											<span>{td("modal.wait")}</span>
										</>
									) : (
										<span>{td("modal.save")}</span>
									)}
								</Button>

								<DrawerClose asChild>
									<Button
										disabled={isLoading}
										size="lg"
										variant="outline"
										type="button"
										className="shadow-none bg-card"
									>
										{td("modal.close")}
									</Button>
								</DrawerClose>
							</DrawerFooter>
						</div>
					</div>
				</DrawerContent>
			</DrawerPrimitive.NestedRoot>
		</div>
	);
});

const ProfileSection = memo(function Component() {
	// ===== TRANSLATIONS =====
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const { t: tpp } = useTranslation("appointments-form", {
		keyPrefix: "patient_profile",
	});
	const { props: globalProps } = usePage<PatientIndexGlobalPageProps>();

	// ===== FORM & DATA =====
	const form = useFormContext<FormSchema>();

	// ===== CONFIGURATION =====
	const dateOfBirthCalendarProps = useMemo<CalendarProps>(() => {
		return {
			// Show 100 years range
			fromYear: new Date().getFullYear() - DOB_YEAR_RANGE,
			toYear: new Date().getFullYear(),
			// Disable future dates
			disabled: (date) => date >= new Date(),
		};
	}, []);

	// ===== EVENT HANDLERS =====
	// * automatically calculate the age from date of birth changes
	const doUpdateAge = useCallback(
		(value: Date | null | undefined) => {
			if (value) {
				// Calculate age using your custom function
				const age = calculateAge(value);
				// Update the age field in the form
				form.setValue("profile.age", age);
				form.trigger("profile.age");
			} else {
				form.setValue("profile.age", 0);
			}
		},
		[form.setValue, form.trigger],
	);

	// ===== COMPUTED VALUES =====
	const patientGenderOptions = useMemo(
		() => globalProps.optionsData?.patientGenders || [],
		[globalProps.optionsData?.patientGenders],
	);

	return (
		<div className="grid gap-2">
			<p className="text-xs font-semibold tracking-wider text-muted-foreground/75 uppercase">
				{tpl("profile")}
			</p>

			<div className="grid gap-4">
				<FormField
					control={form.control}
					name="profile.fullName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{tpp("fields.name.label")}</FormLabel>
							<FormControl>
								<Input
									{...field}
									type="text"
									autoComplete="name"
									placeholder={tpp("fields.name.placeholder")}
									className="shadow-inner bg-input"
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="flex flex-col gap-3">
					<div className="flex gap-3 items-center">
						<FormField
							control={form.control}
							name="profile.dateOfBirth"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{tpp("fields.dob.label")}</FormLabel>
									<Popover modal>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													type="button"
													variant={"outline"}
													className={cn(
														"relative w-full flex justify-between font-normal shadow-inner bg-input",
														!field.value && "text-muted-foreground",
													)}
												>
													<p>
														{field.value
															? `${format(field.value, "PPP")}`
															: tpp("fields.dob.placeholder")}
													</p>

													{field.value ? (
														// biome-ignore lint/a11y/noStaticElementInteractions: -
														<div
															className="cursor-pointer"
															onClick={(event) => {
																event.preventDefault();
																event.stopPropagation();

																form.setValue(
																	"profile.dateOfBirth",
																	null as unknown as Date,
																);
																doUpdateAge(null);
															}}
															onKeyDown={(e) => {
																e.preventDefault();
																e.stopPropagation();
															}}
														>
															<X className="opacity-50" />
														</div>
													) : (
														<CalendarIcon className="w-4 h-4 ml-auto opacity-75" />
													)}
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent
											className="w-auto p-0"
											align="start"
											side="bottom"
										>
											<Calendar
												{...dateOfBirthCalendarProps}
												initialFocus
												mode="single"
												captionLayout="dropdown"
												selected={new Date(field.value)}
												onSelect={(date) => {
													field.onChange(date);
													doUpdateAge(date);
												}}
												defaultMonth={field.value}
											/>
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="profile.age"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{tpp("fields.age.label")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											readOnly
											type="number"
											min={0}
											step="any"
											value={field?.value != null ? field.value : ""}
											placeholder={tpp("fields.age.placeholder")}
											className="shadow-inner w-fit field-sizing-content bg-input"
										/>
									</FormControl>

									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormDescription className="flex-none -mt-1">
						{tpp("fields.age.description")}
					</FormDescription>
				</div>

				<Deferred
					data={["optionsData"]}
					fallback={
						<div className="flex flex-col self-end gap-3 col-span-full">
							<Skeleton className="w-10 h-4 rounded-md" />
							<div className="grid grid-cols-2 gap-4">
								<Skeleton className="relative w-full rounded-md h-9" />
								<Skeleton className="relative w-full rounded-md h-9" />
							</div>
						</div>
					}
				>
					<FormField
						control={form.control}
						name="profile.gender"
						render={({ field }) => (
							<FormItem className="space-y-3 col-span-full">
								<FormLabel>{tpp("fields.gender.label")}</FormLabel>
								<FormControl>
									<RadioGroup
										onValueChange={field.onChange}
										defaultValue={field.value}
										orientation="horizontal"
										className="grid grid-cols-2 gap-4"
									>
										{patientGenderOptions.map((gender) => (
											<Fragment key={gender}>
												<FormItem className="flex items-start p-3 space-x-3 space-y-0 border rounded-md shadow-inner border-input bg-input">
													<FormControl>
														<RadioGroupItem value={gender} />
													</FormControl>
													<FormLabel className="flex items-center gap-1 font-normal capitalize">
														{getGenderIcon(gender)}
														<span className="uppercase">
															{tpp(
																`fields.gender.options.${gender.toLowerCase()}`,
															)}
														</span>
													</FormLabel>
												</FormItem>
											</Fragment>
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</Deferred>
			</div>
		</div>
	);
});

const AddressSection = memo(function Component({
	patient,
}: {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}) {
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { props: globalProps, url: pageURL } =
		usePage<PatientIndexGlobalPageProps>();

	// ===== STATE MANAGEMENT =====
	const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
	const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
	const [selectedAddress, setSelectedAddress] = useState<{
		active: boolean;
		address: PatientActiveAddress;
		addressId: number;
		id: number;
		patientId: number;
		updatedAt: string;
	} | null>(null);
	const [isAddressFormLoading, setIsAddressFormLoading] = useState(false);
	const [isGeocoding, setIsGeocoding] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [addressToDelete, setAddressToDelete] = useState<{
		id: number;
		address: string;
	} | null>(null);

	// ===== FORM CONFIGURATION =====
	const addressForm = useForm<AddressFormSchema>({
		resolver: zodResolver(ADDRESS_FORM_SCHEMA),
		defaultValues: {
			address: "",
			postalCode: "",
			coordinate: "",
			locationId: 0,
			notes: "",
		},
		mode: "onChange",
	});

	// ===== EFFECTS =====
	// Reset address form when selected address changes
	useEffect(() => {
		if (selectedAddress) {
			const latitude = selectedAddress.address.latitude;
			const longitude = selectedAddress.address.longitude;

			addressForm.reset({
				address: selectedAddress.address.address,
				postalCode: selectedAddress?.address?.postalCode || "",
				coordinate:
					latitude !== null && longitude !== null
						? `${latitude}, ${longitude}`
						: "",
				locationId: selectedAddress.address.locationId,
				notes: selectedAddress?.address?.notes || "",
			});
		}
	}, [selectedAddress, addressForm]);

	// ===== API HANDLERS =====
	const handleSetActiveAddress = async (patientAddress: any) => {
		if (!patientAddress) return;

		setIsAddressFormLoading(true);
		try {
			console.log("Setting address as active:", {
				patientAddressId: patientAddress.id,
				currentActive: patientAddress.active,
			});

			// Generate the submit URL
			const baseURL =
				globalProps.adminPortal.router.adminPortal.patientManagement.index;
			const submitURL = `${baseURL}/${patient.id}`;
			const { queryParams } = populateQueryParams(pageURL);
			const { fullUrl } = populateQueryParams(submitURL, queryParams);

			// Define the payload for setting active address
			const payload = deepTransformKeysToSnakeCase({
				patient: {
					profile: {
						id: patient.id,
						name: patient.name,
						dateOfBirth: patient.dateOfBirth,
						gender: patient.gender,
					},
					setActiveAddress: {
						patientAddressId: patientAddress.id,
						active: !patientAddress.active,
					},
				},
			});

			// Make the API call using Inertia.js
			router.put(fullUrl, payload, {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "patients", "selectedPatient"],
				onSuccess: () => {
					toast.success(
						patientAddress.active
							? "Address deactivated successfully"
							: "Address set as active successfully",
					);
				},
				onError: (errors: any) => {
					console.error("Failed to set active address:", errors);
					toast.error("Failed to update address status");
				},
			});

			console.log("Active address update submitted...");
		} catch (error) {
			console.error("Failed to set active address:", error);
			toast.error("Failed to update address status");
		} finally {
			setIsAddressFormLoading(false);
		}
	};

	const handleAddressSubmit = async (data: AddressFormSchema) => {
		if (!selectedAddress) return;

		setIsAddressFormLoading(true);
		try {
			console.log("Updating address:", {
				addressId: selectedAddress.addressId,
				...data,
			});

			// Generate the submit URL
			const baseURL =
				globalProps.adminPortal.router.adminPortal.patientManagement.index;
			const submitURL = `${baseURL}/${patient.id}`;
			const { queryParams } = populateQueryParams(pageURL);
			const { fullUrl } = populateQueryParams(submitURL, queryParams);

			// Define the submit config
			const submitConfig = {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "patients", "selectedPatient"],
				onSuccess: (_page: any) => {
					// Close the address drawer but keep parent drawer open
					setIsEditDrawerOpen(false);

					// Show success toast
					toast.success("Address updated successfully");
				},
				onError: (errors: any) => {
					console.error("Failed to update address:", errors);
					toast.error("Failed to update address");
				},
				onFinish: () => {
					// Always close drawer when request finishes
					setIsEditDrawerOpen(false);
				},
			} as any;

			// Parse coordinate string to numbers for the payload
			let latitude = 0;
			let longitude = 0;
			if (data.coordinate) {
				const [lat, lng] = data.coordinate
					.split(",")
					.map((s) => parseFloat(s.trim()));
				if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
					latitude = lat;
					longitude = lng;
				}
			}

			// Define the payload
			const payload = deepTransformKeysToSnakeCase({
				patient: {
					profile: {
						id: patient.id,
						name: patient.name,
						dateOfBirth: patient.dateOfBirth,
						gender: patient.gender,
					},
					patientAddress: {
						id: selectedAddress.id, // Use patient_address ID, not address ID
						address: data.address,
						postalCode: data.postalCode,
						latitude,
						longitude,
						locationId: data.locationId,
						notes: data.notes,
					},
				},
			});

			router.put(fullUrl, payload, submitConfig);

			console.log("Address update submitted...");
		} catch (error) {
			console.error("Failed to update address:", error);
		} finally {
			setIsAddressFormLoading(false);
		}
	};

	// Handle view on Google Maps for patient address
	const handleViewAddressOnGoogleMaps = useCallback((patientAddress: any) => {
		if (
			patientAddress?.address?.latitude !== null &&
			patientAddress?.address?.longitude !== null
		) {
			const { latitude, longitude } = patientAddress.address;
			window.open(
				`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
				"_blank",
			);
		}
	}, []);

	// ===== DELETE ADDRESS HANDLER =====
	const handleDeleteAddress = async (patientAddress: any) => {
		if (!patientAddress) return;

		// Set up confirmation dialog
		setAddressToDelete({
			id: patientAddress.id,
			address: patientAddress.address?.address || "Unknown address",
		});
		setDeleteConfirmOpen(true);
	};

	const confirmDeleteAddress = async () => {
		if (!addressToDelete) return;

		setIsAddressFormLoading(true);
		try {
			console.log("Deleting patient address:", {
				patientAddressId: addressToDelete.id,
			});

			// Generate the submit URL
			const baseURL =
				globalProps.adminPortal.router.adminPortal.patientManagement.index;
			const submitURL = `${baseURL}/${patient.id}`;
			const { queryParams } = populateQueryParams(pageURL);
			const { fullUrl } = populateQueryParams(submitURL, queryParams);

			// Define the payload for deleting address
			const payload = deepTransformKeysToSnakeCase({
				patient: {
					profile: {
						id: patient.id,
						name: patient.name,
						dateOfBirth: patient.dateOfBirth,
						gender: patient.gender,
					},
					deletePatientAddress: {
						patientAddressId: addressToDelete.id,
					},
				},
			});

			// Make the API call using Inertia.js
			router.put(fullUrl, payload, {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "patients", "selectedPatient"],
				onSuccess: () => {
					toast.success("Address deleted successfully");
					setDeleteConfirmOpen(false);
					setAddressToDelete(null);
				},
				onError: (errors: any) => {
					console.error("Failed to delete address:", errors);
					toast.error("Failed to delete address");
				},
				onFinish: () => {
					setIsAddressFormLoading(false);
				},
			});

			console.log("Address deletion submitted...");
		} catch (error) {
			console.error("Failed to delete address:", error);
			toast.error("Failed to delete address");
			setIsAddressFormLoading(false);
		}
	};

	return (
		<div className="grid gap-2">
			<div>
				<div className="flex items-center justify-between">
					<p className="text-xs font-semibold tracking-wider text-muted-foreground/75 uppercase">
						{tpl("addresses.label")}
					</p>
					<Button
						variant="ghost-primary"
						size="sm"
						type="button"
						onClick={() => setIsAddAddressOpen(true)}
					>
						<PlusIcon className="w-4 h-4" />
						Add
					</Button>
				</div>
			</div>

			{/* Display Patient Addresses */}
			{!patient.patientAddresses || patient.patientAddresses.length === 0 ? (
				<div className="text-center py-4 text-muted-foreground text-sm">
					No addresses available
				</div>
			) : (
				<div className="max-h-[35dvh] overflow-y-auto">
					{patient.patientAddresses.map((patientAddress) => (
						<Item key={patientAddress.id} variant="outline">
							<ItemContent>
								<ItemTitle>
									<p className="line-clamp-2 truncate font-medium text-pretty break-all">
										{patientAddress.address?.address}
									</p>
									{patientAddress.address?.postalCode && (
										<p className="text-muted-foreground text-xs">
											{patientAddress.address.postalCode}
										</p>
									)}
								</ItemTitle>
								{patientAddress.address?.location && (
									<ItemDescription className="text-pretty">
										{[
											patientAddress.address.location.city,
											patientAddress.address.location.state,
											patientAddress.address.location.country,
										]
											.filter(Boolean)
											.join(", ")}
									</ItemDescription>
								)}
							</ItemContent>
							<ItemActions>
								{patientAddress.active ? (
									<CheckCircleIcon className="text-primary shrink-0" />
								) : null}

								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="h-8 w-8 p-0 shrink-0"
										>
											<EllipsisVerticalIcon className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" side="bottom">
										<DropdownMenuItem
											onSelect={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setSelectedAddress(patientAddress);
												setIsEditDrawerOpen(true);
											}}
										>
											<PencilIcon className="h-4 w-4" />
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={(e) => {
												e.preventDefault();
												e.stopPropagation();
												handleViewAddressOnGoogleMaps(patientAddress);
											}}
											disabled={
												patientAddress?.address?.latitude === null ||
												patientAddress?.address?.longitude === null
											}
										>
											<MapPin className="h-4 w-4" />
											View on Google Maps
										</DropdownMenuItem>
										{!patientAddress.active ? (
											<DropdownMenuItem
												onSelect={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleSetActiveAddress(patientAddress);
												}}
											>
												<CheckIcon className="h-4 w-4" />
												Set as Active
											</DropdownMenuItem>
										) : (
											<DropdownMenuItem
												disabled
												className="opacity-50 cursor-not-allowed"
											>
												<CheckIcon className="h-4 w-4" />
												Is Active
											</DropdownMenuItem>
										)}
										{!patientAddress.active && (
											<DropdownMenuItem
												onSelect={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleDeleteAddress(patientAddress);
												}}
												className="text-destructive focus:text-destructive"
											>
												<Trash2 className="h-4 w-4" />
												Delete
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							</ItemActions>
						</Item>
					))}
				</div>
			)}

			{/* Add New Address Section */}
			<AddAddressSection
				patient={patient}
				open={isAddAddressOpen}
				onOpenChange={setIsAddAddressOpen}
				onSuccess={() => {
					// Refresh the patient data to show the new address
					router.reload({ only: ["selectedPatient"] });
				}}
			/>

			{/* Nested Drawer for Address Edit */}
			<DrawerPrimitive.NestedRoot
				open={isEditDrawerOpen}
				onOpenChange={setIsEditDrawerOpen}
				setBackgroundColorOnScale
			>
				<DrawerContent
					onInteractOutside={(event) => {
						event.preventDefault();
					}}
				>
					<DrawerHeader className="w-full md:max-w-5xl mx-auto">
						<DrawerTitle>Edit Address</DrawerTitle>
						<DrawerDescription>
							Edit the address details for this patient.
						</DrawerDescription>
					</DrawerHeader>
					<div className="overflow-y-auto overflow-x-hidden max-h-[90dvh]">
						<div className="p-4 w-full md:max-w-5xl mx-auto">
							<Form {...addressForm}>
								<div className="space-y-4">
									<AddressEntryFields
										form={addressForm}
										locations={globalProps.filterOptions?.locations || []}
										confirmCoordinateReset
										onGeocodingChange={setIsGeocoding}
										className="grid md:grid-cols-2 gap-6"
									/>

									<DrawerFooter className="gap-4 lg:flex-row lg:gap-2 lg:justify-end px-0">
										<Button
											size="lg"
											type="button"
											disabled={isAddressFormLoading || isGeocoding}
											onClick={async (e) => {
												e.stopPropagation();
												// Manually trigger form validation and submission
												const isValid = await addressForm.trigger();
												if (isValid) {
													const data = addressForm.getValues();
													handleAddressSubmit(data);
												}
											}}
										>
											{isAddressFormLoading && (
												<LoaderIcon className="h-4 w-4 animate-spin" />
											)}
											Save Changes
										</Button>

										<DrawerClose asChild>
											<Button
												size="lg"
												variant="outline"
												className="shadow-none bg-card"
												type="button"
												disabled={isAddressFormLoading || isGeocoding}
											>
												{td("modal.close")}
											</Button>
										</DrawerClose>
									</DrawerFooter>
								</div>
							</Form>
						</div>
					</div>
				</DrawerContent>
			</DrawerPrimitive.NestedRoot>

			{/* Delete Address Confirmation Dialog */}
			<AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Address</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete this address? This action cannot
							be undone.
							<br />
							<br />
							<strong>Address:</strong> {addressToDelete?.address}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isAddressFormLoading}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDeleteAddress}
							disabled={isAddressFormLoading}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isAddressFormLoading && (
								<LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
							)}
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
});
